import { useState, useRef, useEffect } from 'react'
import { faEarthAmerica } from '@fortawesome/pro-solid-svg-icons'

import { REGIONS } from './constants'
import { mapFromRawData } from '../table/table-mapping'
import { ControlBar } from '../control-bar'
import { chartFlipperService } from '../chart-flipper.service'
import { blockDataService } from '../block-data.service'
import { ChartExporter } from '../chart-exporter'
import { ChartLoader } from '../chart-loader'
import { MapBlockChart } from './map-block-chart'
import { reportLayoutService } from '../report-layout.service'
import { mapBlockService } from './map-block.service'
import { reportDataSetService } from '../../editor/report-data-set.service'

const DEFAULT_WIDTH = 1024
const DEFAULT_MAP_WIDTH = '100%'
const DEFAULT_MAP_HEIGHT = 500

export const MapBlock = function ({
  blockKey,
  dataKey,
  report,
  strataDefinition,
  sliderDefinition,
  filterDefinition,
  tableDefinition,
  parentControlBar,
  parentBlock,
  parentBlockSelections,
  valueRange: valueRangeFromProp,
  showTableToggle = true,
  region,
  width = DEFAULT_MAP_WIDTH,
  height = DEFAULT_MAP_HEIGHT,
  splitChart,
  ...props
}) {
  const rawData = useRef([])
  const referenceData = useRef()
  const valueRange = useRef(valueRangeFromProp)

  const [chartData, setChartData] = useState()
  const [selections, setSelections] = useState()
  const [splitChartSelections, setSplitChartSelections] = useState(
    reportDataSetService.getFilterDefinitionSplitChartSelections(filterDefinition)
  )
  const [propOverrides, setPropOverrides] = useState()

  const [showExport, setShowExport] = useState(false)
  const [showTable, setShowTable] = useState(false)
  const [tableData, setTableData] = useState()

  props = initializeProps(props, propOverrides)

  const tableDefinitionRef = useRef(chartFlipperService.applyTableDefinitionDefaults(tableDefinition, {
    primaryLabel: mapBlockService.getTablePrimaryLabel(region, props.regionLevel),
    valueField: props.dataDefinition.valueField,
    valueFormat: props.valueType
  }))

  useEffect(() => {
    updateChart(selections)
  }, [props.valueType, props.valueLabel])

  function initializeProps (props, propOverrides) {
    // Add default prop values
    props = {
      dataDefinition: { emptyValue: 'NA', regionField: 'region', valueField: 'n' },
      region: region || REGIONS.NC,
      ...props
    }

    // Apply any prop overrides
    return reportLayoutService.applyPropOverrides(props, propOverrides)
  }

  function onDataLoad (data, refData) {
    rawData.current = splitChart ? data : [data]
    if (refData) {
      referenceData.current = blockDataService.transformRefData(refData)
    }
    updateChart(selections)
  }

  function updateChart (currentSelections) {
    if (!rawData.current || !rawData.current[0] || (splitChart && !rawData.current[1])) {
      return
    }

    if (sliderDefinition && sliderDefinition.key) {
      // When there is a slider, we need to calculate the value range if not passed into the block
      // This is to prevent the range from changing as you move the slider
      if (valueRangeFromProp === undefined) {
        valueRange.current = calculateValueRange(rawData.current, props.dataDefinition)
      }
    }

    const newChartData = []
    const newTableData = []
    rawData.current.forEach((data) => {
      let filteredData = blockDataService.filterBlockDataByKeyValue(data, sliderDefinition && sliderDefinition.key, currentSelections.slider?.value)
      const thresholdFilters = Object.values(currentSelections.thresholdFilters)
      if (thresholdFilters.length > 0) {
        filteredData = blockDataService.filterBlockDataByRefData(filteredData, props.dataDefinition.regionField, referenceData.current, thresholdFilters)
      }
      newChartData.push(transformMapData(filteredData, props.dataDefinition))

      if (showTableToggle) {
        const tableData = mapBlockService.transformToTableData(
          filteredData,
          region,
          props.regionLevel,
          props.dataDefinition.regionField,
          props.dataDefinition.valueField,
          props.valueLabel,
          {
            value2Field: props.dataDefinition.value2Field,
            value2Label: props.value2Label,
            referenceData: referenceData.current,
            thresholdFilters: filterDefinition && filterDefinition.thresholdFilters
          }
        )
        const cellFormats = mapBlockService.getTableCellFormat(props.valueType, props.value2Type)
        const primaryLabel = tableDefinitionRef.current.props.primaryLabel
        const formattedTableData = mapFromRawData(tableData, { cellFormats, primaryLabel })

        if (formattedTableData.columns.length > 5) {
          tableDefinitionRef.current.props.dataColumnWidth = 160
        }

        newTableData.push(formattedTableData)
      }
    })
    setChartData(newChartData)
    setTableData(newTableData)
  }

  function onControlBarChange (selections, changes) {
    if (changes.tableToggle) {
      setShowTable(selections.tableToggle.on)
      return // table toggle change will always be alone
    }

    if (changes.props) {
      setPropOverrides(selections.props)
    }

    if (changes.slider || changes.threshold) {
      updateChart(selections)
    }

    if (changes.display || changes.strata || changes.filters) {
      setSelections(selections)
    }
  }

  function onControlBarToggle ({ type, on }) {
    switch (type) {
      case 'export':
        setShowExport(on)
        break
      case 'table':
        setShowTable(on)
        break
    }
  }

  return <div className="report-full-width">
    <ControlBar
      strataDefinition={strataDefinition}
      sliderDefinition={sliderDefinition}
      filterDefinition={filterDefinition}
      parentControlBar={parentControlBar}
      parentBlock={parentBlock}
      parentBlockSelections={parentBlockSelections}
      tableToggle={{
        enabled: showTableToggle,
        on: showTable,
        iconOn: faEarthAmerica,
        tooltipOn: 'Show Map'
      }}
      exportToggle={{
        enabled: true,
        on: showExport
      }}
      onChange={onControlBarChange}
      onToggle={onControlBarToggle}
    />
    <ChartLoader
      report={report}
      blockKey={blockKey}
      dataKey={dataKey}
      sliderDefinition={sliderDefinition}
      selections={selections}
      splitChart={splitChart}
      splitChartSelections={splitChartSelections}
      width={DEFAULT_WIDTH}
      initialHeight={height}
      onDataLoad={onDataLoad}
    >
      <MapBlockChart
        dataKey={dataKey}
        chartData={chartData}
        tableData={tableData}
        region={region}
        valueRange={valueRange.current}
        referenceData={referenceData.current}
        showTable={showTable}
        showTableToggle={showTableToggle}
        tableDefinition={tableDefinition}
        filterDefinition={filterDefinition}
        onSplitFilterChange={setSplitChartSelections}
        splitChart={splitChart}
        width={width}
        height={height}
        {...props}
      />
    </ChartLoader>
    <ChartExporter
      selections={selections}
      isOpen={showExport}
      onClose={() => setShowExport(false)}
    >
      <MapBlockChart
        dataKey={dataKey}
        chartData={chartData}
        tableData={tableData}
        region={region}
        valueRange={valueRange.current}
        referenceData={referenceData.current}
        showTable={showTable}
        showTableToggle={showTableToggle}
        tableDefinition={tableDefinition}
        filterDefinition={filterDefinition}
        onSplitFilterChange={setSplitChartSelections}
        splitChart={splitChart}
        exporting={true}
        width={width}
        height={height}
        {...props}
      />
    </ChartExporter>
  </div>
}

/**
 * Transform raw data into the data structure required by the map
 */
function transformMapData (data, dataDefinition) {
  const { regionField, valueField, value2Field } = dataDefinition

  if (data.length < 1) return {}

  return data.reduce((result, { [regionField]: region, [valueField]: value, [value2Field]: value2 }) => {
    return Object.assign(result, { [region]: { value, value2 } })
  }, {})
}

function calculateValueRange (data, dataDefinition) {
  const { valueField } = dataDefinition

  if (!data.find((d) => d.length > 0)) return undefined

  const values = data.reduce((values, d) => {
    if (d && d.length) {
      values.push(...d.map((x) => x[valueField]))
    }
    return values
  }, [])

  return {
    min: Math.min(...values),
    max: Math.max(...values)
  }
}
